/**
 * Client-side image resize/compression before case document uploads.
 * Final size must stay within `DOCUMENT_UPLOAD_MAX_BYTES` (6 MB policy).
 */

import {
  DOCUMENT_UPLOAD_MAX_BYTES,
  validateClientDocumentFile,
} from "./documentUploadPolicy";

const MB = 1024 * 1024;
/** Re-export for callers that import max size from this module. */
export { DOCUMENT_UPLOAD_MAX_BYTES };

const AS_IS_MAX_BYTES = 2 * MB;
const COMPRESS_HIGH_TIER_MIN_BYTES = 4 * MB;

const MAX_WIDTH_PX = 2000;
const QUALITY_STANDARD = 0.85;
const QUALITY_HEAVY = 0.75;

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error("Compression produced no data"));
        else resolve(blob);
      },
      "image/jpeg",
      quality
    );
  });
}

function jpegOutputName(originalName: string): string {
  const base = originalName.replace(/\.[^/.]+$/, "").trim() || "document";
  return `${base}.jpg`;
}

/**
 * Raster images suitable for canvas (excludes SVG — not reliably drawable).
 */
export function isCompressibleRasterImage(file: File): boolean {
  const t = (file.type || "").toLowerCase();
  if (!t.startsWith("image/")) return false;
  if (t === "image/svg+xml") return false;
  return true;
}

async function compressImageFileToJpeg(
  file: File,
  options: { maxWidth: number; quality: number }
): Promise<File> {
  const img = await loadImageFromFile(file);
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error("Invalid image dimensions");

  const { maxWidth, quality } = options;
  if (w > maxWidth) {
    h = Math.round((h * maxWidth) / w);
    w = maxWidth;
  }

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unsupported");

  ctx.drawImage(img, 0, 0, w, h);
  const blob = await canvasToJpegBlob(canvas, quality);
  const name = jpegOutputName(file.name);
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

export type PrepareDocumentUploadFileResult =
  | { ok: true; file: File }
  | { ok: false; message: string };

/**
 * Validates policy, then may return a smaller JPEG for large raster photos.
 */
export async function prepareDocumentUploadFile(file: File): Promise<PrepareDocumentUploadFileResult> {
  const initial = validateClientDocumentFile(file);
  if (initial.ok === false) return { ok: false, message: initial.message };

  if (!isCompressibleRasterImage(file) || file.size <= AS_IS_MAX_BYTES) {
    return { ok: true, file };
  }

  const quality =
    file.size >= COMPRESS_HIGH_TIER_MIN_BYTES ? QUALITY_HEAVY : QUALITY_STANDARD;

  try {
    const compressed = await compressImageFileToJpeg(file, {
      maxWidth: MAX_WIDTH_PX,
      quality,
    });
    if (compressed.size > DOCUMENT_UPLOAD_MAX_BYTES) {
      return {
        ok: false,
        message: `Each file must be at most ${DOCUMENT_UPLOAD_MAX_BYTES / MB} MB (could not compress enough).`,
      };
    }
    const after = validateClientDocumentFile(compressed);
    if (after.ok === false) return { ok: false, message: after.message };
    return { ok: true, file: compressed };
  } catch {
    return {
      ok: false,
      message:
        "Could not process this image. Try another JPG/GIF/TIF or upload a PDF.",
    };
  }
}
